// deps
import React, { ReactElement, useCallback, useContext, useState, useEffect, useMemo, memo } from "react";
import { NavigationScreenLinkTypes, NavigationTypes } from "@swiggy-private/native-bridge";

// context
import CommonHeaderContent from "@cart-client/components/common-header-content/CommonHeaderContent";
import { CartHeaderContext } from "@cart-client/contexts/CartHeaderContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// utils
import {
    getBridgeSdk,
    useLocalStorageBasedFrequencyCapping,
    SessionVisibility,
    isPopFlow,
} from "@swiggy-private/checkout-core";
import {
    triggerHepticFeedBack,
    updateStatusBarCleanup,
    updateStatusBarColor,
} from "@cart-client/helpers/IncognitoCartHelper";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";

// ui components
import { CartHeaderContent } from "@swiggy-private/cart-ui";

// analytics
import { clickCartHeaderDropdown, impressionCartHeader } from "@cart-client/analytics/events";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { useIncognitoModeAnalytics } from "@cart-client/hooks/useIncognitoModeAnalytics";

// types
import { CartUIWrapperProps } from "../cart-ui-wrapper/types";

// constants
import { INCOGNITO_TOGGLE_LOTTIE_STORAGE_KEY } from "./constants";
import { CartAddressContext } from "@cart-client/contexts/CartAddressContext";
import { useAddressNudgeConfig } from "@cart-client/hooks/useAddressNudgeConfig";
import { CartHeaderLottieContext } from "@cart-client/contexts/CartHeaderLottieContext";
import { LottieType } from "@cart-client/components/common-header/components/helper";
import { LottieCartType } from "@cart-client/hooks/useCommonCartheaderLottie/constants";
import { isCartHeaderTypeLottie } from "@cart-client/helpers/cartHeaderLottie";

export interface ScrollableDivElement extends HTMLDivElement {
    getScrollPosition: () => number;
}

function CartHeaderContentContainer(props: Omit<CartUIWrapperProps, "children">): ReactElement | null {
    const {
        restaurantName,
        restaurantId,
        selectedAddress,
        onAddressDropdownClick,
        qFoodSLA,
        isPageScrolled,
        showAddressWidgetTop,
        disableRedirectionToMenu,
        irctcData,
        showGreenSubtitle,
        incognitoModeToggleHandler,
        cartIncognitoModeXp,
        cartId,
        isIncognitoCart,
        goToHome,
    } = props;
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const [shouldPlay, setShouldPlay] = useState(false);
    const { onClickToggleHandler, onImpressionToggleSwitch } = useIncognitoModeAnalytics(cartId || "");

    const { cartHeaderState } = useContext(CartHeaderContext);
    const { cartHeaderLottieState } = useContext(CartHeaderLottieContext);
    const { cartAddressState } = useContext(CartAddressContext);
    const { data } = useContext(CartFlowContext);
    const hasStamp = useMemo(
        () => cartHeaderLottieState?.lottieArray?.some((lottie) => lottie.type === LottieType.STAMP),
        [cartHeaderLottieState?.lottieArray],
    );
    const is99Store = useMemo(
        () => cartHeaderLottieState?.cartType === LottieCartType._99STORE,
        [cartHeaderLottieState?.cartType],
    );
    const isCartHeaderTypeLottieExists = useMemo(
        () => isCartHeaderTypeLottie(cartHeaderLottieState?.cartType || ""),
        [cartHeaderLottieState?.cartType],
    );
    const isGiftingCart = useMemo(() => isGiftingCartSource(data?.cartSource), [data?.cartSource]);

    const showAddressNudgeIcon = cartAddressState?.addressNudgeConfig?.showAddressNudgeIcon;

    const handleToggleClick = useCallback(() => {
        triggerHepticFeedBack();
        incognitoModeToggleHandler?.(!isIncognitoCart);
        setShouldPlay(true);
        onClickToggleHandler(isIncognitoCart || false);
    }, [incognitoModeToggleHandler, isIncognitoCart, onClickToggleHandler]);

    const { shouldProceed: isSessionCapReached } = useLocalStorageBasedFrequencyCapping({
        key: INCOGNITO_TOGGLE_LOTTIE_STORAGE_KEY,
        sessionVisibility: SessionVisibility.EVERYTIME_PER_SESSION,
    });

    const isWithinSessionLimit = useMemo(() => isSessionCapReached(), [isSessionCapReached]);
    const isPopCart = isPopFlow();

    useEffect(() => {
        setShouldPlay(isIncognitoCart || false);
    }, [isIncognitoCart]);

    useEffect(() => {
        updateStatusBarColor(isIncognitoCart, showAddressWidgetTop);

        return () => {
            updateStatusBarCleanup();
        };
    }, [isIncognitoCart, showAddressWidgetTop]);

    const onImpressionCartHeader = useCallback(() => {
        impressionCartHeader(impressionEvent);
    }, [impressionEvent]);

    const onClickCartHeaderDropdown = useCallback(() => {
        clickCartHeaderDropdown(clickEvent);
    }, [clickEvent]);

    const onRestaurantNameClick = useCallback(() => {
        if (disableRedirectionToMenu) return;
        if (isGiftingCart) return;
        if (isPopCart) {
            goToHome?.();
        } else {
            getBridgeSdk().navigateToScreen({
                type: NavigationTypes.SCREEN,
                link: NavigationScreenLinkTypes.MENU,
                params: {
                    id: String(restaurantId),
                    fromCart: true,
                },
            });
        }
    }, [disableRedirectionToMenu, goToHome, isGiftingCart, isPopCart, restaurantId]);

    useAddressNudgeConfig();

    if (restaurantName) {
        const isAddressServicable = selectedAddress && selectedAddress.delivery_valid === 1;

        if (showAddressWidgetTop) {
            return (
                <>
                    <CartHeaderContent
                        isPageScrolled={isPageScrolled}
                        restaurantName={restaurantName}
                        selectedAddress={selectedAddress}
                        onAddressDropdownClick={onAddressDropdownClick}
                        qFoodSLA={isAddressServicable ? qFoodSLA : undefined}
                        handleRestaurantNameClick={onRestaurantNameClick}
                        onImpressionCartHeaderContent={onImpressionCartHeader}
                        onClickCartHeaderDropdown={onClickCartHeaderDropdown}
                        irctcData={irctcData}
                        showGreenSubtitle={showGreenSubtitle}
                        shouldPlay={shouldPlay}
                        cartIncognitoModeXp={cartIncognitoModeXp}
                        onImpressionToggleSwitch={onImpressionToggleSwitch}
                        isWithinSessionLimit={isWithinSessionLimit}
                        isIncognitoCart={isIncognitoCart}
                        onToggleClick={handleToggleClick}
                        updateStatusBarCleanup={updateStatusBarCleanup}
                        isPopCart={isPopCart}
                        themeChange={cartHeaderState?.themeChange}
                        lottieThemeChange={cartHeaderLottieState?.themeChange}
                        isOtg={cartHeaderState?.isOtg}
                        isLottieAnimation={
                            isCartHeaderTypeLottieExists
                                ? cartHeaderLottieState?.startAnimation || cartHeaderLottieState?.showFinalState
                                : cartHeaderState?.startAnimation || cartHeaderState?.showFinalState
                        }
                        isBolt={cartHeaderState?.isBolt}
                        showAddressNudgeIcon={showAddressNudgeIcon}
                        hasStamp={hasStamp}
                        is99Store={is99Store}
                        isGiftingCart={isGiftingCart}
                    />
                </>
            );
        } else {
            return (
                <CommonHeaderContent
                    title={restaurantName}
                    subtitle={""}
                    grayscale
                    handleTitleClick={onRestaurantNameClick}
                />
            );
        }
    }
    return null;
}

export default memo(CartHeaderContentContainer);
